import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { Pill } from "@/components/ui/pill";
import { formatRelativeTime } from "@/lib/display-labels";
import { useLineBatches } from "./hooks";
import { originStatusLabel } from "./origin-labels";
import { BatchEditDialog } from "./BatchEditDialog";

const STATUS: Record<string, { label: string; tone: "success" | "warning" | "neutral" }> = {
  ACTIVE: { label: "Em execução", tone: "success" },
  PAUSED: { label: "Pausada", tone: "warning" },
  COMPLETED: { label: "Encerrada", tone: "neutral" },
};

/**
 * Spec 084 — as últimas bateladas da linha, com o que cada uma carrega. É o lugar de corrigir a
 * batelada ENCERRADA (só o gestor, FR-012), e o sinal de quantas estão com obrigatória faltando.
 */
export function BatchesCard({ lineId }: { lineId: string }) {
  const query = useLineBatches(lineId);
  const [editing, setEditing] = useState<string | null>(null);
  const data = query.data;

  return (
    <section id="bateladas" aria-labelledby="batches-h2" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="batches-h2" className="text-[18px] font-semibold tracking-tight text-text-primary">
          Bateladas
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {data && data.pendingOrigins > 0 ? (
            <Pill tone="warning" withDot>
              {data.pendingOrigins} com origem pendente (30 dias)
            </Pill>
          ) : null}
          {data && data.missingRequired > 0 ? (
            <Pill tone="warning" withDot>
              {data.missingRequired} com característica faltando (30 dias)
            </Pill>
          ) : null}
        </div>
      </div>
      <Card className="rounded-2xl px-[22px] py-4">
        {query.isError ? (
          <Notify.QueryError
            error={query.error}
            onRetry={() => void query.refetch()}
            variant="inline"
          />
        ) : query.isLoading || !data ? (
          <p className="text-[13px] text-text-secondary">Carregando bateladas…</p>
        ) : data.items.length === 0 ? (
          <Notify.Empty
            title="Nenhuma batelada ainda"
            description="Cada execução desta linha é uma batelada. Agende uma ordem e ela aparece aqui quando começar."
          />
        ) : (
          <ul className="divide-y divide-border-default">
            {data.items.map((b) => (
              <li key={b.runId} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div className="min-w-0 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[13px] text-text-primary">{b.batchCode}</span>
                    <Pill tone={STATUS[b.status]?.tone ?? "neutral"} withDot>
                      {STATUS[b.status]?.label ?? b.status}
                    </Pill>
                    <span className="text-[11px] text-text-tertiary">
                      {formatRelativeTime(b.startedAt)}
                    </span>
                    {b.originStatus === "PENDING" ? (
                      <Pill tone="warning" withDot>
                        {originStatusLabel(b.originStatus)}
                      </Pill>
                    ) : null}
                  </div>
                  <p className="truncate text-[12px] text-text-secondary">
                    {b.characteristics.length === 0
                      ? "Sem característica nesta linha."
                      : b.characteristics
                          .map(
                            (c) =>
                              `${c.name}: ${
                                c.state === "NONE"
                                  ? "não informado"
                                  : c.state === "MIXED"
                                    ? `misto (${c.labels.join(", ")})`
                                    : c.labels.join(", ")
                              }`
                          )
                          .join(" · ")}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setEditing(b.runId)}>
                  Ver
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {editing ? (
        <BatchEditDialog lineId={lineId} runId={editing} onClose={() => setEditing(null)} />
      ) : null}
    </section>
  );
}
